import { apiClient } from "@lib/axios";

const ERROR_CODE: number[] = [400, 429, 0, 409, 500];
const DELAY: number = 3000;
const RETRY: number = 3;

// Utility function to handle retries with exponential backoff
const withRetry = async <T>(
  operation: () => Promise<T>,
  maxRetries: number = RETRY,
  delay: number = DELAY,
  errorCodes: number[] = ERROR_CODE,
): Promise<T> => {
  let lastError: Error | unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error: unknown) {
      lastError = error;

      // Check if error status code is in the retry list
      const errorStatus =
        (error as { response?: { status?: number }; status?: number })?.response
          ?.status ||
        (error as { status?: number })?.status ||
        0;
      const shouldRetry =
        errorCodes.includes(errorStatus) &&
        import.meta.env.MODE === "production";

      // If this is the last attempt or error shouldn't be retried, throw the error
      if (attempt === maxRetries || !shouldRetry) {
        throw error;
      }

      // Wait before retrying with exponential backoff
      const waitTime = delay * 2 ** attempt;
      await new Promise((resolve) => setTimeout(resolve, waitTime));
    }
  }

  throw lastError;
};

export const analyticsApi = {
  postHubspotEvent: async (
    event: string,
    props: Record<string, unknown> = {},
  ) => {
    return withRetry(async () => {
      const response = await apiClient.post<void, Record<string, unknown>>(
        "/integrations/hubspot-analytics/event",
        { event, props },
      );
      return response;
    });
  },
  postHubspotProperty: async (property: Record<string, unknown>) => {
    return withRetry(async () => {
      const response = await apiClient.post<void, Record<string, unknown>>(
        "/integrations/hubspot-analytics/property",
        { property },
      );
      return response;
    });
  },
};
